<script setup lang="ts">
import type { BetaSubscriptionContract } from '@/types/beta-dashboard'

const props = defineProps<{
    contract: BetaSubscriptionContract
}>()

const formatCurrency = (value: string | number) => {
    const parsed = typeof value === 'number'
        ? value
        : Number(String(value).replace(',', '.'))

    if (!Number.isFinite(parsed)) return String(value)

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    }).format(parsed)
}
</script>

<template>
    <article class="beta-subscription-product">
        <div class="beta-subscription-product__heading">
            <span class="beta-subscription-product__icon" aria-hidden="true">✓</span>
            <div>
                <small>{{ props.contract.planName || 'Produto contratado' }}</small>
                <h3>{{ props.contract.name }}</h3>
            </div>
            <strong>{{ formatCurrency(props.contract.priceValue) }}</strong>
        </div>

        <dl class="beta-subscription-product__details">
            <div v-if="props.contract.priceName">
                <dt>Plano</dt>
                <dd>{{ props.contract.priceName }}</dd>
            </div>
            <div v-if="props.contract.priceCicle">
                <dt>Ciclo</dt>
                <dd>{{ props.contract.priceCicle }}</dd>
            </div>
            <div v-if="props.contract.payMethod">
                <dt>Pagamento</dt>
                <dd>{{ props.contract.payMethod }}</dd>
            </div>
            <div v-if="props.contract.status">
                <dt>Status</dt>
                <dd>{{ props.contract.status }}</dd>
            </div>
        </dl>

        <a
            v-if="props.contract.url"
            class="beta-subscription-product__link"
            :href="props.contract.url"
            target="_blank"
            rel="noopener noreferrer"
        >
            <span>{{ props.contract.url }}</span>
            <span aria-hidden="true">↗</span>
        </a>
    </article>
</template>

<style scoped>
.beta-subscription-product {
    display: grid;
    gap: 13px;
    min-width: 0;
    border: 1px solid #e1e7ef;
    border-radius: 18px;
    background: #fff;
    padding: 15px;
}

.beta-subscription-product__heading {
    display: grid;
    min-width: 0;
    grid-template-columns: 38px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
}

.beta-subscription-product__icon {
    display: grid;
    width: 38px;
    height: 38px;
    place-items: center;
    border-radius: 12px;
    background: color-mix(in srgb, var(--main-color) 10%, #eff4f8);
    color: var(--main-color);
    font-size: 14px;
    font-weight: 900;
}

.beta-subscription-product__heading small,
.beta-subscription-product__heading h3 {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-subscription-product__heading small {
    color: #8694a6;
    font-size: 7px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.beta-subscription-product__heading h3 {
    margin-top: 2px;
    color: #2d4561;
    font-size: 13px;
    font-weight: 850;
}

.beta-subscription-product__heading > strong {
    color: #203a57;
    font-size: 12px;
    font-weight: 900;
    white-space: nowrap;
}

.beta-subscription-product__details {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
}

.beta-subscription-product__details > div {
    min-width: 0;
    border-radius: 10px;
    background: #f6f8fb;
    padding: 8px 9px;
}

.beta-subscription-product__details dt {
    color: #8a97a8;
    font-size: 7px;
    font-weight: 800;
    text-transform: uppercase;
}

.beta-subscription-product__details dd {
    overflow: hidden;
    margin-top: 2px;
    color: #4d6077;
    font-size: 9px;
    font-weight: 750;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-subscription-product__link {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    color: var(--main-color);
    font-size: 9px;
    font-weight: 800;
}

.beta-subscription-product__link span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 560px) {
    .beta-subscription-product__heading {
        grid-template-columns: 38px minmax(0, 1fr);
    }

    .beta-subscription-product__heading > strong {
        grid-column: 2;
    }

    .beta-subscription-product__details {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
